import { ArrowLeft, ArrowUpRight, Check, Linkedin } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SeoHead from "@/seo/SeoHead";

export default function SeoLandingPage({ page, pathname }) {
  return (
    <div id="top" className="grain min-h-screen bg-ink text-ivory">
      <SeoHead page={page} pathname={pathname} />
      <Nav />
      <main>
        <header className="border-b border-surface-2">
          <div className="mx-auto max-w-[1200px] px-6 pb-24 pt-40 md:px-16 md:pb-32 md:pt-48">
            <a
              href="/"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-mutedfg transition-colors hover:text-brass"
            >
              <ArrowLeft size={14} /> Main profile
            </a>
            <div className="mt-14 text-[11px] uppercase tracking-[0.28em] text-brass">
              {page.eyebrow}
            </div>
            <h1 className="mt-6 max-w-5xl font-serif-display text-5xl font-light leading-[0.95] tracking-[-0.025em] text-ivory md:text-7xl lg:text-8xl">
              {page.h1}<span className="text-brass italic">.</span>
            </h1>
            <p className="mt-10 max-w-3xl text-lg font-light leading-relaxed text-mutedfg md:text-xl">
              {page.intro}
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <a
                href="/#contact"
                className="group inline-flex items-center gap-3 bg-brass px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-ink transition-transform hover:-translate-y-0.5"
              >
                Discuss a mandate <ArrowUpRight size={16} />
              </a>
              <a
                href="/Asad_Hatimi_CV.pdf"
                className="inline-flex items-center gap-3 border border-surface-2 px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-ivory transition-colors hover:border-brass hover:text-brass"
              >
                View curriculum
              </a>
            </div>
          </div>
        </header>

        <section aria-label="Practice evidence" className="border-b border-surface-2">
          <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-px bg-surface-2 px-6 md:grid-cols-4 md:px-16">
            {page.proof.map((item) => (
              <div key={item} className="bg-ink px-6 py-8 text-sm font-light text-ivory">
                <span className="mb-3 block h-1.5 w-1.5 bg-brass" />
                {item}
              </div>
            ))}
          </div>
        </section>

        <section className="border-b border-surface-2">
          <div className="mx-auto max-w-[1200px] px-6 py-24 md:px-16 md:py-32">
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-12 md:col-span-4">
                <div className="text-[11px] uppercase tracking-[0.28em] text-brass">Mandate outcomes</div>
                <h2 className="mt-5 font-serif-display text-4xl font-light leading-tight text-ivory">
                  What the engagement is designed to deliver.
                </h2>
              </div>
              <ul className="col-span-12 md:col-span-7 md:col-start-6">
                {page.outcomes.map((outcome) => (
                  <li
                    key={outcome}
                    className="flex gap-4 border-b border-surface-2 py-5 text-base font-light leading-relaxed text-mutedfg"
                  >
                    <Check className="mt-1 shrink-0 text-brass" size={16} />
                    <span>{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="border-b border-surface-2">
          <div className="mx-auto max-w-[1200px] px-6 py-24 md:px-16 md:py-32">
            {page.sections.map((section, index) => (
              <article
                key={section.heading}
                className="grid grid-cols-12 gap-8 border-t border-surface-2 py-12 first:border-t-0 first:pt-0"
              >
                <div className="col-span-12 text-[11px] uppercase tracking-[0.28em] text-brass md:col-span-2">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div className="col-span-12 md:col-span-4">
                  <h2 className="font-serif-display text-3xl font-light leading-tight text-ivory md:text-4xl">
                    {section.heading}
                  </h2>
                </div>
                <div className="col-span-12 space-y-5 text-base font-light leading-relaxed text-mutedfg md:col-span-6">
                  {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="border-b border-surface-2">
          <div className="mx-auto max-w-[1000px] px-6 py-24 md:px-16 md:py-32">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brass">Frequently asked questions</div>
            <h2 className="mt-5 font-serif-display text-4xl font-light text-ivory md:text-5xl">
              Before the first conversation.
            </h2>
            <div className="mt-12">
              {page.faqs.map((faq) => (
                <details key={faq.question} className="group border-t border-surface-2 py-6 last:border-b">
                  <summary className="cursor-pointer list-none font-serif-display text-2xl font-light text-ivory transition-colors group-open:text-brass">
                    {faq.question}
                  </summary>
                  <p className="mt-4 max-w-3xl font-light leading-relaxed text-mutedfg">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-surface-2">
          <div className="mx-auto max-w-[1200px] px-6 py-24 md:px-16 md:py-28">
            <div className="flex flex-col justify-between gap-8 border border-surface-2 p-8 md:flex-row md:items-center md:p-12">
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-brass">Start with the evidence</div>
                <h2 className="mt-4 max-w-3xl font-serif-display text-3xl font-light leading-tight text-ivory md:text-5xl">
                  Share the mandate, current position and decision that cannot wait.
                </h2>
              </div>
              <a
                href="/#contact"
                className="inline-flex shrink-0 items-center gap-3 bg-brass px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-ink"
              >
                Contact Asad <ArrowUpRight size={16} />
              </a>
            </div>
            <a
              href="https://www.linkedin.com/in/asad-hatimi-program-leader"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-mutedfg hover:text-brass"
            >
              <Linkedin size={14} /> Continue on LinkedIn
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
